import * as styles from "./ContactAddressDialog.css";

import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { type RefObject, useState } from "react";

import { Button } from "@frontend/components/atoms/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@frontend/components/atoms/Dialog";
import { Fieldset } from "@frontend/components/atoms/Fieldset";
import { Icon } from "@frontend/components/atoms/Icon";
import { ContactField } from "@frontend/shared/contact-field";
import { Trans } from "@lingui/react/macro";

import { useContactAddressForm } from "../hooks/useContactAddressForm";
import { useContactAddresses } from "../hooks/useContactAddresses";
import { hasValidContactAddress } from "../schemas/contactAddress";
import { locationApi } from "../services";
import {
  contactAddressContactIdAtom,
  contactAddressCreateNewAtom,
  hideContactAddressAtom,
  isContactAddressVisibleAtom,
  selectExistingContactAddressAtom,
} from "../stores";
import type { LocationAddressDetail, LocationTypedAddress } from "../types";
import { AddressFields } from "./AddressFields";
import { ExistingAddressPicker } from "./ExistingAddressPicker";

export type ContactAddressDialogProps = {
  onSubmit?: (address: LocationTypedAddress | LocationAddressDetail) => void;
  onCancel?: () => void;
  originRef?: RefObject<DOMRect | null>;
};

export function ContactAddressDialog({
  onSubmit,
  onCancel,
  originRef,
}: ContactAddressDialogProps) {
  const isVisible = useAtomValue(isContactAddressVisibleAtom);
  const fixedContactId = useAtomValue(contactAddressContactIdAtom);
  const isContactFixed = null != fixedContactId;
  const hide = useSetAtom(hideContactAddressAtom);

  const { contactAddressForm, submit } = useContactAddressForm();

  const [contactId] = useAtom(contactAddressForm.fields.contactId.atom);
  const existingAddressId = useAtomValue(
    contactAddressForm.fields.existingAddressId.atom,
  );
  const selectExistingAddress = useSetAtom(selectExistingContactAddressAtom);
  const [createNew, setCreateNew] = useAtom(contactAddressCreateNewAtom);
  const addresses = useContactAddresses(contactId);

  const [saving, setSaving] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleToggleCreateNew = (checked: boolean) => {
    setCreateNew(checked);

    if (checked) {
      selectExistingAddress(null);
    } else {
      const first = addresses[0];

      selectExistingAddress(first ? first.id : null);
    }
  };

  const handleSelectAddress = (addressId: number) => {
    setCreateNew(false);
    selectExistingAddress(addressId);
  };

  const handleCancel = () => {
    onCancel?.();
    hide();
  };

  const handleSave = async () => {
    const result = await submit();

    if (!result.success) {
      return;
    }

    const data = result.data;

    const nextContactError =
      null == data.contactId ? "Please select a contact." : null;

    const nextAddressError = hasValidContactAddress(data)
      ? null
      : "Please fill in the street, postal code and city fields.";

    setContactError(nextContactError);
    setAddressError(nextAddressError);

    if (null == data.contactId || nextAddressError) {
      return;
    }

    setSaveError(null);
    setSaving(true);

    try {
      const input = {
        place: data.place || undefined,
        street: data.street,
        extendedAddress: data.extendedAddress || undefined,
        postOfficeBox: data.postOfficeBox || undefined,
        postalCode: data.postalCode,
        city: data.city,
        country: data.country || undefined,
      };

      const address =
        null != data.existingAddressId
          ? await locationApi.updateAddress(data.existingAddressId, input)
          : await locationApi.createAddress({
              contactId: data.contactId,
              ...input,
            });

      onSubmit?.(address);
      hide();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : String(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={isVisible}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleCancel();
        }
      }}
      originRef={originRef}
    >
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle className={styles.titleRow}>
            <Icon name="MapPinHouse" className={styles.titleIcon} />
            {null == contactId ? (
              <Trans>Select a contact</Trans>
            ) : createNew ? (
              <Trans>New address</Trans>
            ) : (
              <Trans>Edit address</Trans>
            )}
          </DialogTitle>
          <DialogClose />
        </DialogHeader>

        <div className={styles.body}>
          <Fieldset orientation="horizontal" variant="card" size="md">
            {!isContactFixed && (
              <ContactField
                field={contactAddressForm.fields.contactId}
                error={contactError}
                onContactSelected={() => selectExistingAddress(null)}
              />
            )}

            <ExistingAddressPicker
              createNew={createNew}
              onToggleCreateNew={handleToggleCreateNew}
              existingAddressId={existingAddressId ?? null}
              addresses={addresses}
              onSelectAddress={handleSelectAddress}
            />
          </Fieldset>

          <AddressFields
            fields={contactAddressForm.fields}
            disabled={!createNew}
          />

          {addressError && <p role="alert">{addressError}</p>}
          {saveError && <p role="alert">{saveError}</p>}
        </div>

        <DialogFooter>
          <Button variant="neutral" onClick={handleCancel}>
            <Trans>Cancel</Trans>
          </Button>
          <Button variant="primary" loading={saving} onClick={handleSave}>
            <Trans>Save</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
